import React, {useLayoutEffect, useRef, useState} from 'react';
import cx from 'classnames';

import {useCursorPos} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {EffectProps, HoverEffectProps} from './HoverEffect.types';

import * as css from './HoverEffect.css';

const Effect = ({containerRect, variant, colorOverride}: EffectProps) => {
  const innerRef = useRef<HTMLSpanElement>(null);
  const {x, y} = useCursorPos({delay: 0});
  const containerLeft = containerRect?.left || 0;
  const containerTop = containerRect?.top || 0;
  const innerRect = innerRef.current?.getBoundingClientRect();
  const normalizedX = x - containerLeft - (innerRect?.width || 0) / 2;
  const normalizedY = y - containerTop - (innerRect?.height || 0) / 2;
  const percentageX = innerRect ? Math.round((normalizedX / innerRect.width) * 100) : 0;
  const percentageY = innerRect ? Math.round((normalizedY / innerRect.height) * 100) : 0;

  return (
    <span
      className={css.effect({variant})}
      style={{
        opacity: percentageX && percentageY ? 1 : 0,
        transform: `translate(${percentageX}%, ${percentageY}%)`,
        borderColor: colorOverride,
      }}
      ref={innerRef}
    />
  );
};

/**
 * HoverEffect adds a ring around the current cursor position when the cursor
 * moves over the element that the `<HoverEffect>` is mounted inside of.
 *
 * Because this component uses an absolutely positioned element for tracking
 * when the element in question is/isn't being hovered, the element this
 * component is a child of should also be positioned (eg, 'relative'). If the
 * element in question is meant to be clickable, make sure you have ordered the
 * DOM accordingly, so that this hover effect does not occlude clicks intended
 * for underlying elements.
 */
export const HoverEffect = ({
  className,
  onClick,
  onMouseOver,
  onMouseLeave,
  variant = 'primary',
  colorOverride,
  ...otherProps
}: HoverEffectProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [containerRect, setContainerRect] = useState<DOMRect | undefined>(undefined);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Using `useLayoutEffect` here so we can perform layout calculations
  // before the browser repaints the screen.
  useLayoutEffect(() => {
    if (isHovered) {
      setContainerRect(containerRef.current?.getBoundingClientRect());
    }
  }, [isHovered]);

  const handleMouseOver = (e: React.MouseEvent<HTMLSpanElement>) => {
    onMouseOver?.(e);
    setContainerRect(containerRef.current?.getBoundingClientRect());

    if (!isHovered) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLSpanElement>) => {
    onMouseLeave?.(e);
    setIsHovered(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    onClick?.(e);
    setIsHovered(false);
  };

  return (
    <span
      className={cx(className, sprinkles({inset: 0, position: 'absolute'}))}
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      {...otherProps}
      ref={containerRef}>
      {isHovered && (
        <Effect containerRect={containerRect} variant={variant} colorOverride={colorOverride} />
      )}
    </span>
  );
};
